/**
 * 平台统一 HTTP 基础设施
 *
 * 全站唯一的 API 请求底层：各业务模块（api.ts / directorApi / canvasAPI / video apiBase 等）
 * 的 authFetch 均委托此处，消除 API Base、鉴权头、401 处理、响应解包的多套副本。
 *
 * 依赖方向：仅依赖 utils/authSession（叶子模块），不得 import 业务模块，避免循环依赖。
 */

import { clearActiveAuthSession, getActiveToken } from '../utils/authSession';

// ============================================================================
// API 基础地址
// ============================================================================

const getApiBase = (): string => {
  // 1. 优先从 window 全局配置读取（运行时注入）
  if (typeof window !== 'undefined' && (window as any).__API_BASE__) {
    return (window as any).__API_BASE__;
  }
  // 2. 从环境变量读取（构建时）
  if (import.meta.env.VITE_API_BASE) {
    return import.meta.env.VITE_API_BASE;
  }
  // 3. 根据当前域名自动判断（生产环境）
  if (typeof window !== 'undefined' && window.location.hostname === 'www.zhitu.app') {
    return '/api';
  }
  // 4. 默认本地开发地址
  return 'http://localhost:3001/api';
};

export const API_BASE = getApiBase();

// ============================================================================
// 错误类型
// ============================================================================

/** 平台统一 API 错误：携带 HTTP 状态、业务错误码与可选元信息 */
export class PlatformApiError extends Error {
  status: number;
  code?: string;
  meta?: Record<string, unknown>;

  constructor(message: string, status: number, code?: string, meta?: Record<string, unknown>) {
    super(message);
    this.name = 'PlatformApiError';
    this.status = status;
    this.code = code;
    this.meta = meta;
  }
}

// ============================================================================
// 请求
// ============================================================================

/** 401 时广播的事件名，PlatformSessionProvider 监听后同步清空 React 状态 */
export const UNAUTHORIZED_EVENT = 'platform:unauthorized';

function notifyUnauthorized(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
}

/**
 * 统一带鉴权请求。
 *
 * - body 为 FormData 时自动不设 Content-Type，保留 fetch 生成的 boundary；
 * - 有活跃 token（主会话或代登录会话）时附加 Bearer；
 * - 401 时清理当前会话并广播 UNAUTHORIZED_EVENT，随后仍返回 Response 由调用方处理。
 */
export async function platformFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = getActiveToken();

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (response.status === 401) {
    clearActiveAuthSession();
    notifyUnauthorized();
  }

  return response;
}

// ============================================================================
// 响应解包
// ============================================================================

/** fallback 消息支持携带响应状态码（如 `请求失败 (${status})`） */
type EnvelopeFallback = string | ((status: number) => string);

function resolveFallback(fallback: EnvelopeFallback, status: number): string {
  return typeof fallback === 'function' ? fallback(status) : fallback;
}

/**
 * 统一响应解包，兼容后端现有全部格式：
 *
 * - HTTP 非 2xx → 抛 PlatformApiError（error/code/meta 来自响应体）；
 * - HTTP 2xx 但 body.ok === false → 同样抛错（视频等模块的历史约定）；
 * - body 含 data 字段（{ ok: true, data } envelope）→ 解包返回 data；
 * - 其余 → 原样返回 body；JSON 解析失败返回 null。
 */
export async function readEnvelope<T = unknown>(
  response: Response,
  fallbackMessage: EnvelopeFallback
): Promise<T> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    /* ignore */
  }

  if (!response.ok) {
    const err = body as { error?: string; code?: string; meta?: Record<string, unknown> } | null;
    throw new PlatformApiError(
      err?.error || resolveFallback(fallbackMessage, response.status),
      response.status,
      err?.code,
      err?.meta
    );
  }

  if (body && typeof body === 'object' && 'ok' in body && (body as { ok?: unknown }).ok === false) {
    const err = body as { error?: string; code?: string };
    throw new PlatformApiError(
      err.error || resolveFallback(fallbackMessage, response.status),
      response.status,
      err.code
    );
  }

  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

// ============================================================================
// SSE 流式读取
// ============================================================================

/**
 * 逐行读取 SSE 响应，把每个 `data:` 行的 payload 交给回调。
 *
 * 跨 chunk 安全：用 buffer 拼接、只消费以 \n 结尾的完整行，流结束后冲刷尾部残留
 * （fetch 的 ReadableStream 可能把一行拆到多个 chunk，直接 split 会丢内容）。
 * 回调抛错会中断读取并向上传播。
 */
export async function readSSEStream(
  response: Response,
  onPayload: (payload: string) => void
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('无法读取流式响应');

  const decoder = new TextDecoder();
  let buffer = '';

  const consumeLine = (line: string) => {
    if (!line.startsWith('data: ')) return;
    onPayload(line.slice(6));
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      // 最后一行可能不完整，留在 buffer 里等下一次拼接后再消费
      buffer = lines.pop() ?? '';
      for (const line of lines) consumeLine(line);
    }

    // 冲刷尾部：解码器残留字节 + buffer 里最后一行（不带换行符）
    buffer += decoder.decode();
    if (buffer) consumeLine(buffer);
  } catch (err) {
    // 回调抛错（业务性中断，如节点被删除）也要取消底层流：
    // 不 cancel 的话连接会保持打开继续下载剩余内容
    void reader.cancel().catch(() => {});
    throw err;
  }
}

/**
 * SSE JSON 事件流：在 readSSEStream 之上处理 [DONE] 与 JSON 解析。
 *
 * - `data: [DONE]` 触发 onDone（flag 防重）；
 * - JSON.parse 失败静默忽略（保持历史语义）；
 * - 流读完但从未收到 [DONE]（网络截断）也触发 onDone，
 *   避免消费方的加载态/按钮复位完全押在 [DONE] 上而永久卡死。
 */
export async function streamJsonEvents(
  response: Response,
  handlers: {
    onEvent: (event: Record<string, unknown>) => void;
    onDone?: () => void;
  }
): Promise<void> {
  let doneNotified = false;
  const notifyDone = () => {
    if (doneNotified) return;
    doneNotified = true;
    handlers.onDone?.();
  };

  await readSSEStream(response, (payload) => {
    if (payload === '[DONE]') {
      notifyDone();
      return;
    }
    try {
      handlers.onEvent(JSON.parse(payload));
    } catch {
      /* ignore */
    }
  });

  // 流自然结束但未见 [DONE]：仍通知完成，保证消费方能复位
  notifyDone();
}
